import { FC, useState, useContext } from 'react';
import { UserContext } from '../../../../../context';
import { Close, Save, Email, Info } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  Box,
  ButtonGroup,
  Button,
  Divider,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { updateContractDates } from '../../../../../fetch';
import {
  IContractDatesPayload,
  IContractDatesValues,
  Maybe,
  IContractDetails,
  EmailType,
  EventType
} from '../../../../../models';
import { Loader } from '../../../../../components';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  formatShortFriendlyDateWithTime,
  formatDBFriendlyDateTime,
  formatDate,
  isAllowedToEditUnderContract
} from '../../../../../helpers';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { UnderContractEmailModal } from '../send-email/under-contract-email-modal';
import { underContractPages } from '../under-contract-details';
import { useFlags } from '../../../../../hooks';
import { addDays, min, setHours, setMinutes } from 'date-fns';

interface IContractDates {
  contractDates: IContractDatesValues;
  isLoadingContractDates: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchContractDates: () => void;
  buildId: string;
  contractDetails: IContractDetails;
  selectedPage: underContractPages;
  handleEmailSentRef: any;
  refetchContractDetails: () => void;
}

const canAppointmentEndBeNull = (date: Maybe<Date>) => {
  if (date === null || date === undefined) return true;

  const dateVal = new Date(date);
  return dateVal.getHours() === 0 && dateVal.getMinutes() === 0;
};
const ContractDatesSchema = Yup.object().shape(
  {
    homeownerOrientation: Yup.string()
      .nullable()
      // Yup.test provides the value testing and the context. We needed this to ensure the types were also compared i.e. string !== Date
      .test(
        'max',
        'Homeowner Orientation must be before Homeowner Celebration',
        (value, context) => {
          if (context?.parent?.homeownerCelebration && value) {
            return new Date(context.parent.homeownerCelebration) > new Date(value);
          } else return true;
        }
      )
      .test(
        'max',
        'Homeowner Orientation must be before the Closing Appointment',
        (value, context) => {
          if (context?.parent?.closingAppointmentStart && value) {
            return new Date(context.parent.closingAppointmentStart) > new Date(value);
          } else return true;
        }
      ),

    homeownerCelebration: Yup.string()
      .nullable()
      .test(
        'min',
        'Homeowner Celebration must be after Homeowner Orientation',
        (value, context) => {
          if (context?.parent?.homeownerOrientation && value) {
            return new Date(context.parent.homeownerOrientation) < new Date(value);
          } else return true;
        }
      )
      .test('max', 'Homeowner Orientation must be before Closing Appointment', (value, context) => {
        if (context?.parent?.closingAppointmentStart && value) {
          const celebrationDate = new Date(value);
          const closingDate = new Date(context.parent.closingAppointmentStart);

          // if the closing event is all day, we want to compare the dates only
          if (context?.parent?.allDayEvent) {
            if (celebrationDate.toDateString() === closingDate.toDateString()) return true;
          }

          return closingDate > celebrationDate;
        } else return true;
      }),
    closingAppointmentStart: Yup.string()
      .nullable()
      .test(
        'maxOrientation',
        'Closing Appointment must be after Homeowner Orientation',
        (value, context) => {
          if (context?.parent?.homeownerOrientation && value) {
            return new Date(context.parent.homeownerOrientation) < new Date(value);
          } else return true;
        }
      )
      .test('max', 'Closing Appointment must be after Homeowner Celebration', (value, context) => {
        if (context?.parent?.homeownerCelebration && value) {
          const celebrationDate = new Date(context.parent.homeownerCelebration);
          const closingDate = new Date(value);

          // if the closing event is all day, we want to compare the dates only
          if (context?.parent?.allDayEvent) {
            if (celebrationDate.toDateString() === closingDate.toDateString()) return true;
          }

          // if the event is not all day, we want to compare the dates and times
          return celebrationDate < closingDate;
        } else return true;
      })
      .notRequired(),

    closingNotes: Yup.string(),
    thirtyDayEmail: Yup.boolean(),
    responseRecieved: Yup.boolean(),
    responseInfo: Yup.string(),
    allDayEvent: Yup.boolean()
  },
  [
    ['homeownerOrientation', 'homeownerCelebration'],
    ['homeownerCelebration', 'homeownerOrientation'],
    ['homeownerCelebration', 'closingAppointmentStart'],
    ['closingAppointmentStart', 'homeownerCelebration'],
    ['closingAppointmentStart', 'homeownerOrientation']
  ]
);

export const ContractDates: FC<IContractDates> = ({
  contractDates,
  isLoadingContractDates,
  setDirtyForm,
  refetchContractDates,
  buildId,
  contractDetails,
  selectedPage,
  handleEmailSentRef,
  refetchContractDetails
}) => {
  const classes = useStyles();
  const userContext = useContext(UserContext);
  const hasEditAccess = isAllowedToEditUnderContract(userContext.user);
  const { enqueueSnackbar } = useSnackbar();
  const [emailType, setEmailType] = useState('');
  const [eventType, setEventType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const featureFlags = useFlags();

  const setSecondsToZero = (date: Maybe<Date>) => {
    if (!date) {
      return null;
    }

    const newDate = new Date(date);
    newDate.setSeconds(0);
    return newDate;
  };

  function addHours(date: Maybe<Date>, hours: number) {
    if (date === null || date === undefined) return date;
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + hours);
    return newDate;
  }

  const getMinDateTime = startDateTime => {
    if (!startDateTime) return null;
    return new Date(startDateTime);
  };

  return (
    <>
      {isLoadingContractDates && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingContractDates && (
        <Formik
          validateOnChange
          enableReinitialize={true}
          initialValues={{
            ...contractDates,
            closingAppointmentStart:
              contractDates?.sageClosingDate && contractDates?.closingAppointmentStart === null
                ? setHours(setMinutes(new Date(contractDates.sageClosingDate), 0), 13) // set to 1pm
                : contractDates?.closingAppointmentStart
                ? new Date(contractDates.closingAppointmentStart)
                : null,
            closingAppointmentEnd: contractDates?.closingAppointmentEnd?.toLocaleString() ?? null,
            homeownerOrientation: contractDates?.homeownerOrientation
              ? new Date(contractDates.homeownerOrientation)
              : null,
            homeownerCelebration: contractDates?.homeownerCelebration
              ? new Date(contractDates.homeownerCelebration)
              : null,
            closingNotes: contractDates?.closingNotes ?? '',
            thirtyDayEmail: contractDates?.thirtyDayEmail ?? false,
            thirtyDayEmailBy: contractDates?.thirtyDayEmailBy,
            thirtyDayEmailOn: contractDates?.thirtyDayEmailOn,
            responseRecieved: contractDates?.responseRecieved ?? false,
            responseInfo: contractDates?.responseInfo ?? '',
            allDayEvent: contractDates?.isClosingAppointmentAllDay ?? false
          }}
          validationSchema={ContractDatesSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IContractDatesPayload = {
                ...values,
                closingAppointmentStart: formatDBFriendlyDateTime(values?.closingAppointmentStart),
                closingAppointmentEnd: !canAppointmentEndBeNull(values?.closingAppointmentStart)
                  ? formatDBFriendlyDateTime(addHours(values?.closingAppointmentStart, 1))
                  : null,
                homeownerOrientation: formatDBFriendlyDateTime(values?.homeownerOrientation),
                homeownerCelebration: formatDBFriendlyDateTime(values?.homeownerCelebration),
                thirtyDayEmail: values?.thirtyDayEmail ?? false,
                responseRecieved: values?.responseRecieved ?? false,
                responseInfo: values?.responseInfo ?? '',
                closingDateLocked: values?.closingDateLocked,
                isClosingAppointmentAllDay: values?.allDayEvent
              };

              await updateContractDates(buildId, data);
              await refetchContractDates();
              enqueueSnackbar('Contract Dates have been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ?? 'Something went wrong. Contract Dates were not updated.',
                {
                  variant: 'error'
                }
              );
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            handleSubmit,
            dirty,
            isValid,
            resetForm
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (
              dirty ||
              (contractDates?.sageClosingDate && contractDates?.closingAppointmentStart === null)
            ) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }
            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant={'body1'}>
                          Estimated Completion Date:{' '}
                          {contractDates?.estimatedCompletionDate
                            ? formatDate(contractDates?.estimatedCompletionDate)
                            : 'No Estimated Completion Date'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant={'body1'}>
                          Sage Closing Date:{' '}
                          {contractDates?.sageClosingDate
                            ? formatDate(contractDates?.sageClosingDate)
                            : 'No Sage Closing Date'}
                        </Typography>
                      </Grid>
                      <Box width="100%" />
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth component="fieldset">
                          <DateTimePicker
                            disabled={!hasEditAccess}
                            label="Homeowner Orientation"
                            timeSteps={{ minutes: 1 }}
                            value={values.homeownerOrientation}
                            maxDateTime={min(
                              [
                                getMinDateTime(values.homeownerCelebration),
                                getMinDateTime(values.closingAppointmentStart)
                              ].filter(Boolean)
                            )}
                            // sets the default time to 10:00 AM
                            referenceDate={setHours(setMinutes(new Date(), 0), 10)}
                            onChange={date => {
                              setFieldValue('homeownerOrientation', date);
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: !!errors?.homeownerOrientation,
                                value: values.homeownerOrientation
                              }
                            }}
                          />
                          {errors?.homeownerOrientation ?? (
                            <FormHelperText error>{errors?.homeownerOrientation}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          disabled={!hasEditAccess || !contractDates.homeownerOrientation}
                          startIcon={<Email />}
                          onClick={() => {
                            setEmailType(EmailType.Invite);
                            setEventType(EventType.HomeownerOrientation);
                            setIsModalOpen(true);
                          }}
                          color="secondary"
                          variant="contained"
                        >
                          {contractDetails?.orientationInviteSent ? 'Update Invite' : 'Invite'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth component="fieldset">
                          <DateTimePicker
                            disabled={!hasEditAccess}
                            label="Homeowner Celebration"
                            timeSteps={{ minutes: 1 }}
                            value={values.homeownerCelebration}
                            minDateTime={getMinDateTime(values.homeownerOrientation)}
                            // For a same day, all day closing event users should still be able to set the time of the orientation
                            maxDateTime={
                              values.allDayEvent
                                ? getMinDateTime(values.closingAppointmentEnd)
                                : getMinDateTime(values.closingAppointmentStart)
                            }
                            // sets the default time to 2:00 PM
                            referenceDate={setHours(setMinutes(new Date(), 0), 14)}
                            onChange={(date: any) => {
                              setFieldValue('homeownerCelebration', date);
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: !!errors?.homeownerCelebration,
                                fullWidth: true
                              }
                            }}
                          />
                          {errors?.homeownerCelebration ?? (
                            <FormHelperText error>{errors?.homeownerCelebration}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          disabled={!hasEditAccess || !contractDates.homeownerCelebration}
                          startIcon={<Email />}
                          onClick={() => {
                            setEmailType(EmailType.Invite);
                            setEventType(EventType.HomeownerCelebration);
                            setIsModalOpen(true);
                          }}
                          color="secondary"
                          variant="contained"
                        >
                          {contractDetails?.celebrationInviteSent ? 'Update Invite' : 'Invite'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth component="fieldset">
                          <DateTimePicker
                            disabled={!hasEditAccess}
                            label="Closing Appointment"
                            timeSteps={{ minutes: 1 }}
                            views={
                              values.allDayEvent
                                ? ['year', 'month', 'day']
                                : ['year', 'month', 'day', 'hours', 'minutes']
                            }
                            value={values.closingAppointmentStart}
                            minDateTime={min(
                              [
                                getMinDateTime(
                                  values.allDayEvent
                                    ? values.homeownerOrientation
                                      ? addDays(values.homeownerOrientation, 1)
                                      : null
                                    : values.homeownerCelebration
                                ),
                                getMinDateTime(
                                  values.allDayEvent
                                    ? values.homeownerCelebration
                                      ? addDays(values.homeownerCelebration, 1)
                                      : null
                                    : values.homeownerOrientation
                                )
                              ].filter(Boolean)
                            )}
                            onChange={(date: any) => {
                              setFieldValue('closingAppointmentStart', setSecondsToZero(date));
                            }}
                            slotProps={{
                              textField: { size: 'small', error: !!errors?.closingAppointmentStart }
                            }}
                          />
                          {errors?.closingAppointmentStart ?? (
                            <FormHelperText error>{errors?.closingAppointmentStart}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} justifyContent={'space-between'}>
                        <Button
                          disabled={!hasEditAccess || !contractDates.closingAppointmentStart}
                          startIcon={<Email />}
                          onClick={() => {
                            setEmailType(EmailType.Invite);
                            setEventType(EventType.ClosingAppointment);
                            setIsModalOpen(true);
                          }}
                          color="secondary"
                          variant="contained"
                        >
                          {contractDetails?.closingInviteSent ? 'Update Invite' : 'Invite'}
                        </Button>
                        <FormControlLabel
                          sx={{ marginLeft: '0' }}
                          color="primary"
                          control={
                            <Checkbox
                              checked={values.allDayEvent}
                              id="all-day-event-checkbox"
                              onChange={() => {
                                setFieldValue('allDayEvent', !values.allDayEvent);
                              }}
                            />
                          }
                          label="Remote Signing"
                        />
                        <FormControlLabel
                          sx={{ marginLeft: '0' }}
                          color="primary"
                          control={
                            <Checkbox
                              checked={values.closingDateLocked}
                              id="closing-date-locked-checkbox"
                              disabled={!featureFlags.closingDateSageOverwrite || !hasEditAccess}
                              onChange={() => {
                                setFieldValue('closingDateLocked', !values.closingDateLocked);
                              }}
                            />
                          }
                          label={
                            <>
                              Lock Closing Appointment{' '}
                              <Tooltip
                                title={
                                  'Clicking the Lock Closing Appointment box will lock this Closing date in Sage'
                                }
                              >
                                <IconButton>
                                  <Info sx={{ display: 'inline-flex' }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          }
                        />
                      </Grid>
                      <Box width="100%" />
                      <Grid item xs={12}>
                        <MuiTextField
                          disabled={!hasEditAccess}
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Closing Notes"
                          name="notes"
                          multiline
                          minRows={3}
                          value={values?.closingNotes}
                          onChange={e => setFieldValue('closingNotes', e.target.value)}
                          error={!!errors?.closingNotes}
                          helperText={errors?.closingNotes}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={!hasEditAccess}
                              checked={values?.thirtyDayEmail}
                              onChange={e => {
                                setFieldValue('thirtyDayEmail', e.target.checked);
                              }}
                            />
                          }
                          label={
                            values?.thirtyDayEmail && contractDates?.thirtyDayEmail
                              ? `30 Day Email (sent ${formatShortFriendlyDateWithTime(
                                  contractDates?.thirtyDayEmailOn
                                )} by ${contractDates.thirtyDayEmailBy})`
                              : '30 Day Email'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} className={classes.leftSpacing}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={!hasEditAccess}
                              checked={values?.responseRecieved}
                              onChange={e => {
                                setFieldValue('responseRecieved', e.target.checked);
                              }}
                            />
                          }
                          label="Response"
                        />
                        <MuiTextField
                          disabled={!hasEditAccess}
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Response Info"
                          name="responseInfo"
                          multiline
                          minRows={3}
                          value={values?.responseInfo}
                          onChange={e => setFieldValue('responseInfo', e.target.value)}
                          error={!!errors?.responseInfo}
                          helperText={errors?.responseInfo}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <UnderContractEmailModal
                    open={isModalOpen}
                    onClose={shouldRefetch => {
                      if (shouldRefetch) {
                        refetchContractDetails();
                        refetchContractDates();
                      }

                      setIsModalOpen(false);
                    }}
                    setIsLoading={() => setisLoading(false)}
                    isLoading={isLoading}
                    buildId={buildId}
                    selectedPage={selectedPage}
                    fetchClientHistory={handleEmailSentRef}
                    emailType={emailType}
                    contractDates={contractDates}
                    refetchContractDetails={refetchContractDetails}
                    eventType={eventType}
                  />
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>

                  <ButtonGroup>
                    <Button
                      disabled={
                        // one off check here if the user first loads the page and the closing appointment is null
                        // and the sage closing date is set. This is to allow the user to see an active save button
                        contractDates.sageClosingDate &&
                        contractDates.closingAppointmentStart === null
                          ? false
                          : !dirty || isSubmitting || !isValid
                      }
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  leftSpacing: {
    marginLeft: '75px',
    '@media (max-width: 600px)': {
      marginLeft: '0'
    }
  }
}));
